import { cn } from 'noddi-ui-common';
import {
  ApiErrorMessageInternal,
  NoddiButton,
  NoddiInfoBanner,
  NoddiLink,
  NoddiSheet,
  useIsMobile
} from 'noddi-ui-web';
import { useState } from 'react';
import { WorkerAvatar } from '../ServiceWorker/WorkerAvatar';
import { DrivenCarsList } from './DrivenCarsList';
import { MemberCarsFooter } from './MemberCarsFooter';
import { MemberCarsPicker } from './MemberCarsPicker';
import { MemberContactSummary } from './MemberContactSummary';
import { MemberRoleSection } from './MemberRoleSection';
import { buildRestrictionReasons, MEMBER_DETAILS_LABEL } from './memberSheetCopy';
import { MemberSheetError } from './MemberSheetError';
import type { MemberSheetPermissions } from './memberSheetPatches';
import type { MemberGroup } from './notificationGroupTypes';
import type { NotificationGroupOverviewTranslations } from './notificationTranslations';
import { RestrictionNote } from './RestrictionNote';
import { SectionHeading } from './SectionHeading';
import { useMemberCarsFlow } from './useMemberCarsFlow';
import { useMemberDrivenCars } from './useMemberDrivenCars';
import { useMemberSheetForm } from './useMemberSheetForm';

const FORM_ID = 'organization-member';
const FOOTER_BUTTON_CLASS = 'w-full flex-1 sm:w-fit';

const TOGGLE_ROW_CLASS = 'rounded-xl border border-border/60 p-3';

type Props = {
  userGroupId: number;
  member: MemberGroup;
  canEditAccount: boolean;
  canEditNotifications: boolean;
  canManageAdmin: boolean;
  canManagePrimaryContact: boolean;
  isOnlyAdmin: boolean;
  canAssignDrivers: boolean;
  /** Leasing company or car dealership: show the employee switch. */
  canAssignMembershipRole?: boolean;
  translations: NotificationGroupOverviewTranslations;
  showMutationToasts: boolean;
  /** Set by hosts that have a member page to link to; without it the footer is Save alone. */
  memberDetailUrl?: string;
  /**
   * Opens the removal flow for this member. Absent when the viewer may not remove
   * them; the label reads "Leave group" when the member is the viewer.
   */
  onRemove?: () => void;
  isSelf?: boolean;
  onClose: () => void;
};

export const OrganizationMemberSheet = ({
  userGroupId,
  member,
  canEditAccount,
  canEditNotifications,
  canManageAdmin,
  canManagePrimaryContact,
  isOnlyAdmin,
  canAssignDrivers,
  canAssignMembershipRole = false,
  translations: t,
  showMutationToasts,
  memberDetailUrl,
  onRemove,
  isSelf = false,
  onClose
}: Props) => {
  const [view, setView] = useState<'member' | 'cars'>('member');
  const isCarsView = view === 'cars';
  const isMobile = useIsMobile();
  // A group needs one admin, so the last one keeps the role until someone else takes it.
  const canEditAdmin = canManageAdmin && !isOnlyAdmin;
  // Phones get a read-only summary first: four inputs push the toggles and cars below the fold.
  const [isEditingContact, setIsEditingContact] = useState(!isMobile && canEditAccount);
  const permissions: MemberSheetPermissions = {
    canEditAccount,
    canEditNotifications,
    canEditAdmin,
    canManagePrimaryContact,
    canAssignMembershipRole
  };
  const { form, userError, memberError } = useMemberSheetForm({
    userGroupId,
    member,
    permissions,
    translations: t,
    showMutationToasts,
    onClose
  });

  const name = `${member.firstName} ${member.lastName}`.trim();
  const accountReason = canEditAccount ? undefined : t.accountOwnedByMember(member.firstName || name);
  const restrictions = buildRestrictionReasons({
    accountReason,
    canEditNotifications,
    canManageAdmin,
    canAssignDrivers,
    translations: t
  });
  const carsFlow = useMemberCarsFlow({ userGroupId, member, translations: t, onSaved: () => setView('member') });
  const canSubmitChanges = canEditAccount || canEditNotifications || canEditAdmin || canManagePrimaryContact;
  const { data: drivenCarsResponse, error: drivenCarsError } = useMemberDrivenCars(
    userGroupId,
    member.userGroupMemberId
  );
  const drivenCars = drivenCarsResponse?.results ?? [];
  // The row the sheet opened from is a snapshot; the driven-cars query refetches after a driver change.
  const drivenCarCount = drivenCarsResponse?.count ?? member.managedCarCount;
  const detailLink = memberDetailUrl ? (
    <NoddiLink to={memberDetailUrl} variant='outline' startIcon='User' className={FOOTER_BUTTON_CLASS}>
      {MEMBER_DETAILS_LABEL}
    </NoddiLink>
  ) : undefined;
  const saveButton = canSubmitChanges ? (
    <form.SubscribeButton formId={FORM_ID} label={t.saveChanges} className={FOOTER_BUTTON_CLASS} />
  ) : undefined;
  const removeButton = onRemove ? (
    <NoddiButton variant='destructiveOutline' startIcon='Trash2' className={FOOTER_BUTTON_CLASS} onClick={onRemove}>
      {isSelf ? t.leaveGroup : t.removeMember}
    </NoddiButton>
  ) : undefined;

  const chrome = isCarsView
    ? {
        icon: (
          <NoddiButton
            variant='ghost'
            size='extraSmall'
            startIcon='ArrowLeft'
            aria-label={t.back}
            onClick={() => (carsFlow.stage === 'review' ? carsFlow.goToChoose() : setView('member'))}
          />
        ),
        title: t.addCars,
        description:
          carsFlow.stage === 'choose' ? t.chooseCarsStep : carsFlow.stage === 'review' ? t.reviewStep : undefined,
        footer: <MemberCarsFooter flow={carsFlow} onLeave={() => setView('member')} translations={t} />
      }
    : {
        icon: <WorkerAvatar name={name} size='md' />,
        title: name,
        description: undefined,
        footer:
          detailLink || saveButton || removeButton ? (
            <div className='flex w-full flex-col-reverse gap-2 sm:flex-row'>
              {removeButton}
              {detailLink}
              {saveButton}
            </div>
          ) : undefined
      };

  return (
    <form.AppForm>
      <NoddiSheet
        open
        onOpenChange={(next) => !next && onClose()}
        title={
          <span className='flex items-center gap-3'>
            {chrome.icon}
            <span>{chrome.title}</span>
          </span>
        }
        description={chrome.description}
        closeText={t.cancel}
        footer={chrome.footer}
      >
        {isCarsView && (
          <MemberCarsPicker
            userGroupId={userGroupId}
            memberName={member.firstName || name}
            flow={carsFlow}
            canEditNotifications={canEditNotifications}
            translations={t}
          />
        )}

        {/* Kept mounted while the car list is open so a half-typed edit survives the trip. */}
        <form
          id={FORM_ID}
          className={cn('flex-col gap-6', isCarsView ? 'hidden' : 'flex')}
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <RestrictionNote leadIn={canSubmitChanges ? undefined : t.viewOnly} reasons={restrictions} />

          <MemberSheetError error={userError} forbiddenMessage={accountReason} />
          <MemberSheetError error={memberError} forbiddenMessage={t.memberEditAdminOnly} />

          <section className='flex flex-col gap-4'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <SectionHeading icon='User'>{t.contactDetails}</SectionHeading>
              {!isEditingContact && canEditAccount && (
                <NoddiButton variant='outline' size='small' startIcon='Edit' onClick={() => setIsEditingContact(true)}>
                  {t.edit}
                </NoddiButton>
              )}
            </div>
            {isEditingContact && canEditAccount ? (
              <>
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                  <form.AppField name='firstName'>{(field) => <field.Input label={t.firstName} />}</form.AppField>
                  <form.AppField name='lastName'>{(field) => <field.Input label={t.lastName} />}</form.AppField>
                </div>
                <form.AppField name='phoneNumber'>
                  {(field) => <field.PhoneInput label={t.phoneNumber} />}
                </form.AppField>
                <form.AppField name='email'>{(field) => <field.Input type='email' label={t.email} />}</form.AppField>
              </>
            ) : (
              <MemberContactSummary member={member} translations={t} />
            )}
          </section>

          <section className='flex flex-col gap-4'>
            <div className='flex flex-col gap-1'>
              <SectionHeading icon='MessageSquare'>{t.notifications}</SectionHeading>
              <p className='text-sm text-muted-foreground'>{t.notificationsDescription}</p>
            </div>
            <div className='flex flex-col gap-2'>
              <div className={TOGGLE_ROW_CLASS}>
                <form.AppField name='smsBookingNotifications'>
                  {(field) => (
                    <field.Switch
                      label={t.bookingSms}
                      description={t.bookingSmsDescription}
                      switchPosition='end'
                      disabled={!canEditNotifications}
                    />
                  )}
                </form.AppField>
              </div>
              <div className={TOGGLE_ROW_CLASS}>
                <form.AppField name='smsRecallProposals'>
                  {(field) => (
                    <field.Switch
                      label={t.serviceReminderSms}
                      description={t.serviceReminderSmsDescription}
                      switchPosition='end'
                      disabled={!canEditNotifications}
                    />
                  )}
                </form.AppField>
              </div>
            </div>
            {/* Mirrors the "SMS turned off" badge in the members table, at the moment it is caused. */}
            <form.Subscribe
              selector={(state) => !state.values.smsBookingNotifications && !state.values.smsRecallProposals}
            >
              {(isSilenced) =>
                isSilenced &&
                drivenCars.length > 0 && (
                  <NoddiInfoBanner variant='warning' texts={[{ value: t.smsTurnedOffWarning }]} />
                )
              }
            </form.Subscribe>
          </section>

          <MemberRoleSection
            form={form}
            translations={t}
            canAssignMembershipRole={canAssignMembershipRole}
            canEditAdmin={canEditAdmin}
            canManageAdmin={canManageAdmin}
            canManagePrimaryContact={canManagePrimaryContact}
            isOnlyAdmin={isOnlyAdmin}
            memberIsPrimaryContact={member.isPrimaryContact}
          />

          <section className='flex flex-col gap-3'>
            {drivenCarsError && <ApiErrorMessageInternal error={drivenCarsError} />}
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <SectionHeading icon='Car'>
                {t.drives}
                <span className='font-normal text-muted-foreground'>{` · ${t.carCount(drivenCarCount)}`}</span>
              </SectionHeading>
              {canAssignDrivers && (
                <NoddiButton variant='outline' size='small' startIcon='Plus' onClick={() => setView('cars')}>
                  {t.addCars}
                </NoddiButton>
              )}
            </div>
            <DrivenCarsList cars={drivenCars} totalCount={drivenCarCount} translations={t} />
          </section>
        </form>
      </NoddiSheet>
    </form.AppForm>
  );
};
